import { useState, useRef, useEffect } from "react";
import { Search, X, Loader2, Globe, Building2, MapPin, Hotel } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { recentSearchesStorage } from "@/utils/localStorage";
import { ContentFallback } from "@/components/ui/content-fallback";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";

export type Suggestion = {
  type: 'country' | 'city' | 'region' | 'hotel';
  name: string;
  regionId?: number;
  hotelId?: string;
  regionType?: string;
  countryCode?: string;
};

interface SearchAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onSelectSuggestion?: (s: Suggestion) => void;
}

export const SearchAutocomplete = ({ value, onChange, placeholder, onSelectSuggestion }: SearchAutocompleteProps) => {
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [countries, setCountries] = useState<Suggestion[]>([]);
  const [cities, setCities] = useState<Suggestion[]>([]);
  const [regions, setRegions] = useState<Suggestion[]>([]);
  const [hotels, setHotels] = useState<Suggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, hasSpaceBelow: true });
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setRecentSearches(recentSearchesStorage.getAll());
  }, []);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Trigger search when value changes - debounced
  const debouncedSearch = useDebouncedCallback((searchValue: string) => {
    if (searchValue.length >= 2) {
      runSearch(searchValue);
    }
  }, 300);

  useEffect(() => {
    if (value.length >= 2) {
      debouncedSearch(value);
    } else if (value.length === 0) {
      setShowResults(false);
      setCountries([]);
      setCities([]);
      setRegions([]);
      setHotels([]);
    }
  }, [value, debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update dropdown position on mobile when it opens - with keyboard detection
  useEffect(() => {
    if (showResults && containerRef.current && window.innerWidth < 768) {
      const updatePosition = () => {
        const rect = containerRef.current!.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          hasSpaceBelow: spaceBelow > 200
        });
      };
      
      updatePosition();
      
      // Listen for keyboard open/close via visualViewport
      window.visualViewport?.addEventListener('resize', updatePosition);
      window.visualViewport?.addEventListener('scroll', updatePosition);
      
      // Close dropdown on scroll (mobile only)
      const handleScroll = () => setShowResults(false);
      window.addEventListener('scroll', handleScroll, { passive: true });
      
      return () => {
        window.visualViewport?.removeEventListener('resize', updatePosition);
        window.visualViewport?.removeEventListener('scroll', updatePosition);
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [showResults]);

  const runSearch = async (query: string) => {
    if (query.trim().length < 2) {
      setCountries([]);
      setCities([]);
      setRegions([]);
      setHotels([]);
      setShowResults(query.length > 0);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('offline-autocomplete', {
        body: { query, language: 'en' }
      });
      
      if (error) {
        console.error('Autocomplete failed:', error);
        throw error;
      }
      
      // Parse normalized response - handle both direct data and nested structures
      const responseData = data;
      const regionsList = Array.isArray(responseData?.regions) ? responseData.regions : [];
      const hotelsList = Array.isArray(responseData?.hotels) ? responseData.hotels : [];

      // Group regions by type using Maps
      const countriesMap = new Map<number, Suggestion>();
      const citiesMap = new Map<number, Suggestion>();
      const regionsMap = new Map<number, Suggestion>();

      // Process offline database regions
      regionsList.forEach((r: any) => {
        // Normalize type to handle different casings (e.g., "City", "city", "country")
        const normalizedType = (r.type || '').toLowerCase().trim();
        const suggestion: Suggestion = {
          type: normalizedType === 'country' ? 'country' : normalizedType === 'city' ? 'city' : 'region',
          name: r.name,
          regionId: r.id,
          regionType: r.type,
          countryCode: r.country_code
        };

        if (normalizedType === 'country') {
          countriesMap.set(r.id, suggestion);
        } else if (normalizedType === 'city') {
          citiesMap.set(r.id, suggestion);
        } else {
          regionsMap.set(r.id, suggestion);
        }
      });

      const countriesArray = Array.from(countriesMap.values());
      const citiesArray = Array.from(citiesMap.values());
      const regionsArray = Array.from(regionsMap.values());
      
      setCountries(countriesArray);
      setCities(citiesArray);
      setRegions(regionsArray);
      
      // Process ETG API hotels
      const apiHotels = hotelsList.map((h: any) => ({ 
        type: 'hotel' as const, 
        name: h.name ?? h.id, 
        hotelId: String(h.id), 
        regionId: h.region_id 
      }));
      
      setHotels(apiHotels);
      setShowResults(true);
      setSelectedIndex(-1);
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        return;
      }
      console.error('Autocomplete failed:', e);
      setCountries([]);
      setCities([]);
      setRegions([]);
      setHotels([]);
      setShowResults(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    onChange(query);
    setSelectedIndex(-1);
    // Debouncing handled by useDebouncedCallback in useEffect
  };

  // Get all suggestions in order for keyboard navigation
  const allSuggestions = [
    ...countries.map(s => ({ ...s, category: 'Countries' })),
    ...cities.map(s => ({ ...s, category: 'Cities' })),
    ...regions.map(s => ({ ...s, category: 'Regions' })),
    ...hotels.map(s => ({ ...s, category: 'Hotels' }))
  ];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || allSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < allSuggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < allSuggestions.length) {
          handleSelect(allSuggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowResults(false);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelect = (s: Suggestion) => {
    onChange(s.name);
    onSelectSuggestion?.(s);
    setShowResults(false);
    recentSearchesStorage.add(s.name);
    setRecentSearches(recentSearchesStorage.getAll());
  };

  const clearRecent = () => {
    recentSearchesStorage.clear();
    setRecentSearches([]);
  };

  return (
    <div className="relative w-full overflow-visible" ref={containerRef} style={{ position: 'relative' }}>
      <div className="relative overflow-visible">
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder || "City, hotel, or vibe"}
          value={value}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => value.length > 0 && setShowResults(true)}
          onKeyDown={handleKeyDown}
          className="w-full border-0 bg-transparent text-base md:text-sm text-ivory placeholder:text-muted-foreground focus:outline-none"
          aria-label="Search destination"
          role="combobox"
          aria-expanded={showResults}
          aria-autocomplete="list"
          autoComplete="on"
          autoCorrect="on"
          autoCapitalize="words"
          inputMode="search"
        />
        {loading && (
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <div className="relative w-4 h-4">
              <div className="absolute inset-0 rounded-full border-2 border-gold/20" />
              <div className="absolute inset-0 rounded-full border-2 border-gold border-t-transparent animate-spin" />
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showResults && (value.length > 0 || recentSearches.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="search-autocomplete-dropdown absolute md:top-full md:left-0 md:right-0 md:mt-3 bg-charcoal/98 backdrop-blur-2xl border border-gold/30 rounded-2xl shadow-2xl shadow-black/50 z-[70] max-h-96 overflow-y-auto gold-scrollbar"
            style={window.innerWidth < 768 ? {
              position: 'fixed',
              top: `max(${dropdownPosition.top + 8}px, calc(env(safe-area-inset-top) + 80px))`,
              left: 'max(16px, env(safe-area-inset-left))',
              right: 'max(16px, env(safe-area-inset-right))',
              maxHeight: 'calc(60vh - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
            } : undefined}
            role="listbox"
          >
            {value.length === 0 && recentSearches.length > 0 && (
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-ivory">Recent searches</h4>
                  <button
                    onClick={clearRecent}
                    className="text-xs text-ivory/60 hover:text-gold transition-colors font-medium"
                  >
                    Clear all
                  </button>
                </div>
                {recentSearches.map((search, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelect({ type: 'city', name: search })}
                    className="w-full text-left px-4 py-2.5 rounded-xl hover:bg-gold/10 text-ivory/80 hover:text-gold transition-colors text-sm mb-1"
                  >
                    {search}
                  </button>
                ))}
              </div>
            )}

            {/* Show helper text for 1 character */}
            {value.length === 1 && (
              <div className="p-8 text-center">
                <p className="text-ivory/60 text-sm">Type at least 2 characters to search</p>
              </div>
            )}

            {/* Loading state */}
            {loading && value.length > 1 && (
              <div className="p-3">
                <ContentFallback type="section" count={1} />
              </div>
            )}

            {!loading && value.length > 1 && (
              <>
                {countries.length > 0 && (
                  <div className="p-5 border-b border-ivory/10">
                    <h4 className="text-xs font-semibold text-ivory/60 mb-3 uppercase tracking-wider px-1">Countries</h4>
                    {countries.map((result, idx) => {
                      const globalIdx = idx;
                      return (
                        <button
                          key={`country-${idx}`}
                          onClick={() => handleSelect(result)}
                          className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center gap-3 mb-1 ${
                            selectedIndex === globalIdx ? 'bg-gold/20 text-gold shadow-lg shadow-gold/10' : 'hover:bg-gold/10 text-ivory/90 hover:text-gold'
                          }`}
                          role="option"
                          aria-selected={selectedIndex === globalIdx}
                        >
                          <Globe className="w-5 h-5 text-gold flex-shrink-0" />
                          <span className="truncate font-medium">{result.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {cities.length > 0 && (
                  <div className="p-5 border-b border-ivory/10">
                    <h4 className="text-xs font-semibold text-ivory/60 mb-3 uppercase tracking-wider px-1">Cities</h4>
                    {cities.map((result, idx) => {
                      const globalIdx = countries.length + idx;
                      return (
                        <button
                          key={`city-${idx}`}
                          onClick={() => handleSelect(result)}
                          className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center gap-3 mb-1 ${
                            selectedIndex === globalIdx ? 'bg-gold/20 text-gold shadow-lg shadow-gold/10' : 'hover:bg-gold/10 text-ivory/90 hover:text-gold'
                          }`}
                          role="option"
                          aria-selected={selectedIndex === globalIdx}
                        >
                          <Building2 className="w-5 h-5 text-gold flex-shrink-0" />
                          <span className="truncate font-medium">{result.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {regions.length > 0 && (
                  <div className="p-5 border-b border-ivory/10">
                    <h4 className="text-xs font-semibold text-ivory/60 mb-3 uppercase tracking-wider px-1">Regions</h4>
                    {regions.map((result, idx) => {
                      const globalIdx = countries.length + cities.length + idx;
                      return (
                        <button
                          key={`region-${idx}`}
                          onClick={() => handleSelect(result)}
                          className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center gap-3 mb-1 ${
                            selectedIndex === globalIdx ? 'bg-gold/20 text-gold shadow-lg shadow-gold/10' : 'hover:bg-gold/10 text-ivory/90 hover:text-gold'
                          }`}
                          role="option"
                          aria-selected={selectedIndex === globalIdx}
                        >
                          <MapPin className="w-5 h-5 text-gold flex-shrink-0" />
                          <span className="truncate font-medium">{result.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {hotels.length > 0 && (
                  <div className="p-5">
                    <h4 className="text-xs font-semibold text-ivory/60 mb-3 uppercase tracking-wider px-1">Hotels</h4>
                    {hotels.map((result, idx) => {
                      const globalIdx = countries.length + cities.length + regions.length + idx;
                      return (
                        <button
                          key={`hotel-${idx}`}
                          onClick={() => handleSelect(result)}
                          className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-200 flex items-center gap-3 mb-1 ${
                            selectedIndex === globalIdx ? 'bg-gold/20 text-gold shadow-lg shadow-gold/10' : 'hover:bg-gold/10 text-ivory/90 hover:text-gold'
                          }`}
                          role="option"
                          aria-selected={selectedIndex === globalIdx}
                        >
                          <Hotel className="w-5 h-5 text-gold flex-shrink-0" />
                          <span className="truncate font-medium">{result.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {countries.length === 0 && cities.length === 0 && regions.length === 0 && hotels.length === 0 && !loading && (
                  <div className="p-10 text-center">
                    <p className="text-ivory/60 text-sm mb-2">No results found</p>
                    <p className="text-ivory/40 text-xs">Try searching for a different destination</p>
                  </div>
                )}

                {/* Powered by caption */}
                {(countries.length > 0 || cities.length > 0 || regions.length > 0 || hotels.length > 0) && (
                  <div className="px-5 py-3 border-t border-ivory/10 text-center">
                    <p className="text-xs text-ivory/40">Powered by offline database • 984 hotels</p>
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
